import LegalLayout, { Section, Card, Grid, InfoBox, ContactCTA } from "@/components/chalky/LegalLayout"

interface Props {
  last_updated: string
}

export default function Confidentiality({ last_updated }: Props) {
  return (
    <LegalLayout
      title="Politique de"
      titleHighlight="Confidentialité"
      subtitle="Votre vie privée est importante pour nous. Cette politique explique comment CHALKY collecte, utilise et protège vos données personnelles."
      lastUpdated={last_updated}
    >
      <Section number="1" title="Introduction">
        <p className="text-slate-400 leading-relaxed mb-4">
          CHALKY EURL, située au 26 B rue Aristide Briand, 44230 Saint-Sébastien-sur-Loire, s'engage à protéger la confidentialité des données personnelles de ses utilisateurs conformément au Règlement Général sur la Protection des Données (RGPD).
        </p>
        <p className="text-slate-400 leading-relaxed">
          Cette politique de confidentialité décrit comment nous collectons, utilisons, stockons et protégeons vos informations personnelles.
        </p>
      </Section>

      <Section number="2" title="Données personnelles collectées">
        <Grid>
          <Card>
            <p className="font-medium text-white mb-3">Données de contact</p>
            <ul className="list-disc pl-5 space-y-1 text-sm text-slate-400">
              <li>Nom et prénom</li>
              <li>Adresse e-mail</li>
              <li>Numéro de téléphone</li>
              <li>Nom de l'entreprise</li>
            </ul>
          </Card>
          <Card>
            <p className="font-medium text-white mb-3">Données techniques</p>
            <ul className="list-disc pl-5 space-y-1 text-sm text-slate-400">
              <li>Adresse IP</li>
              <li>Type de navigateur et version</li>
              <li>Pages visitées et durée de visite</li>
              <li>Données de géolocalisation approximative</li>
            </ul>
          </Card>
        </Grid>
        <InfoBox icon="fa-solid fa-circle-info" title="Information importante">
          Nous ne collectons que les données strictement nécessaires au fonctionnement de nos services et à l'amélioration de votre expérience utilisateur.
        </InfoBox>
      </Section>

      <Section number="3" title="Finalités du traitement">
        <p className="text-slate-400 mb-4">Vos données personnelles sont traitées pour les finalités suivantes :</p>
        <ul className="list-disc pl-6 space-y-3 text-slate-400">
          <li><strong className="text-slate-200">Gestion des demandes de contact :</strong> répondre à vos questions et demandes d'information</li>
          <li><strong className="text-slate-200">Prestation de services :</strong> fournir nos services de développement web et applications</li>
          <li><strong className="text-slate-200">Communication commerciale :</strong> vous informer de nos actualités et services (avec votre consentement)</li>
          <li><strong className="text-slate-200">Amélioration de nos services :</strong> analyser l'utilisation de notre site pour l'optimiser</li>
          <li><strong className="text-slate-200">Obligations légales :</strong> respecter nos obligations comptables et fiscales</li>
        </ul>
      </Section>

      <Section number="4" title="Base légale du traitement">
        <p className="text-slate-400 mb-4">Le traitement de vos données repose sur :</p>
        <ul className="list-disc pl-6 space-y-3 text-slate-400">
          <li><strong className="text-slate-200">Exécution d'un contrat :</strong> pour la prestation de nos services</li>
          <li><strong className="text-slate-200">Intérêt légitime :</strong> pour l'amélioration de nos services et la sécurité</li>
          <li><strong className="text-slate-200">Consentement :</strong> pour la communication commerciale</li>
          <li><strong className="text-slate-200">Obligation légale :</strong> pour le respect des obligations comptables et fiscales</li>
        </ul>
      </Section>

      <Section number="5" title="Durée de conservation">
        <Grid cols={2}>
          <Card>
            <p className="text-xs uppercase text-slate-500 font-medium mb-2">Données clients</p>
            <p className="text-sm text-slate-300">Conservées pendant la durée de la relation commerciale + 3 ans</p>
          </Card>
          <Card>
            <p className="text-xs uppercase text-slate-500 font-medium mb-2">Données prospects</p>
            <p className="text-sm text-slate-300">Conservées 3 ans à compter du dernier contact</p>
          </Card>
          <Card>
            <p className="text-xs uppercase text-slate-500 font-medium mb-2">Données comptables</p>
            <p className="text-sm text-slate-300">Conservées 10 ans conformément aux obligations légales</p>
          </Card>
          <Card>
            <p className="text-xs uppercase text-slate-500 font-medium mb-2">Logs techniques</p>
            <p className="text-sm text-slate-300">Conservés 12 mois maximum</p>
          </Card>
        </Grid>
      </Section>

      <Section number="6" title="Vos droits">
        <p className="text-slate-400 mb-6">Conformément au RGPD, vous disposez des droits suivants :</p>
        <Grid>
          <Card>
            <p className="font-medium text-white mb-1">
              <i className="fa-solid fa-eye text-slate-500 mr-2" />
              Droit d'accès
            </p>
            <p className="text-sm text-slate-400">Connaître les données que nous détenons sur vous</p>
          </Card>
          <Card>
            <p className="font-medium text-white mb-1">
              <i className="fa-solid fa-pen text-slate-500 mr-2" />
              Droit de rectification
            </p>
            <p className="text-sm text-slate-400">Corriger des données inexactes ou incomplètes</p>
          </Card>
          <Card>
            <p className="font-medium text-white mb-1">
              <i className="fa-solid fa-trash-can text-slate-500 mr-2" />
              Droit à l'effacement
            </p>
            <p className="text-sm text-slate-400">Supprimer vos données personnelles</p>
          </Card>
          <Card>
            <p className="font-medium text-white mb-1">
              <i className="fa-solid fa-download text-slate-500 mr-2" />
              Droit à la portabilité
            </p>
            <p className="text-sm text-slate-400">Récupérer vos données dans un format structuré</p>
          </Card>
        </Grid>
        <InfoBox icon="fa-solid fa-envelope" title="Exercer vos droits">
          Pour exercer ces droits, contactez-nous à :{" "}
          <a href="mailto:hello@chalky.fr" className="text-slate-200 hover:text-white underline">hello@chalky.fr</a>
        </InfoBox>
      </Section>

      <Section number="7" title="Sécurité des données">
        <p className="text-slate-400 mb-6">Nous mettons en œuvre des mesures techniques et organisationnelles appropriées pour protéger vos données :</p>
        <Grid cols={3}>
          <Card className="text-center">
            <i className="fa-solid fa-shield-halved text-slate-500 mb-3 block" />
            <p className="font-medium text-white mb-2">Chiffrement SSL/TLS</p>
            <p className="text-sm text-slate-400">Toutes les communications sont sécurisées</p>
          </Card>
          <Card className="text-center">
            <i className="fa-solid fa-lock text-slate-500 mb-3 block" />
            <p className="font-medium text-white mb-2">Accès restreint</p>
            <p className="text-sm text-slate-400">Accès limité aux personnes autorisées</p>
          </Card>
          <Card className="text-center">
            <i className="fa-solid fa-database text-slate-500 mb-3 block" />
            <p className="font-medium text-white mb-2">Sauvegardes</p>
            <p className="text-sm text-slate-400">Sauvegardes régulières et sécurisées</p>
          </Card>
        </Grid>
      </Section>

      <Section number="8" title="Partage des données">
        <p className="text-slate-400 mb-4">CHALKY ne vend, ne loue et ne partage pas vos données personnelles avec des tiers, sauf dans les cas suivants :</p>
        <ul className="list-disc pl-6 space-y-2 text-slate-400 mb-4">
          <li>Avec votre consentement explicite</li>
          <li>Pour répondre à une obligation légale</li>
          <li>Pour protéger nos droits et notre sécurité</li>
          <li>Avec nos prestataires techniques (sous contrat de confidentialité)</li>
        </ul>
        <InfoBox icon="fa-solid fa-circle-exclamation" title="Aucune cession commerciale">
          Nous ne vendons jamais vos données à des fins commerciales ou publicitaires.
        </InfoBox>
      </Section>

      <Section number="9" title="Contact et réclamations">
        <Grid>
          <Card>
            <p className="font-medium text-white mb-4">Contactez-nous</p>
            <div className="space-y-2 text-sm text-slate-400">
              <p><a href="mailto:hello@chalky.fr" className="text-slate-300 hover:text-white transition-colors">hello@chalky.fr</a></p>
              <p>+33 6 74 23 60 80</p>
              <p>26 B rue Aristide Briand<br />44230 Saint-Sébastien-sur-Loire, France</p>
            </div>
          </Card>
          <Card>
            <p className="font-medium text-white mb-4">Autorité de contrôle</p>
            <p className="text-sm text-slate-400 mb-2">En cas de désaccord, vous pouvez saisir la CNIL :</p>
            <div className="text-sm text-slate-400">
              <p className="text-slate-300">Commission Nationale de l'Informatique et des Libertés</p>
              <p>3 Place de Fontenoy - TSA 80715</p>
              <p>75334 PARIS CEDEX 07</p>
              <a href="https://www.cnil.fr" target="_blank" rel="noopener noreferrer" className="text-slate-300 hover:text-white underline mt-2 inline-block">
                www.cnil.fr
              </a>
            </div>
          </Card>
        </Grid>
      </Section>

      <ContactCTA
        title="Des questions sur notre politique de confidentialité ?"
        subtitle="Notre équipe est à votre disposition pour répondre à toutes vos questions."
      />
    </LegalLayout>
  )
}
